import { useState } from "react";
import {
  ChevronLeft,
  Mail,
  HelpCircle,
  Search,
  Rocket,
  MessageSquare,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/accordion";

const HelpSupport = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const faqs = [
    {
      question: "How do I scan my meals?",
      answer:
        "Tap the camera icon in the bottom navigation, point your camera at your meal, and take a photo. Our AI will automatically identify the foods and estimate nutritional values.",
    },
    {
      question: "How is my Bite Balance score calculated?",
      answer:
        "Your Bite Balance score is calculated based on multiple factors including your food quality, meal timing, macronutrient balance, and consistency with your health goals.",
    },
    {
      question: "Can I connect my fitness tracker?",
      answer:
        "Yes! Go to Profile > Connected Devices to link your Fitbit or other compatible fitness trackers. This allows us to incorporate your activity data for more accurate recommendations.",
    },
    {
      question: "How do I update my health goals?",
      answer:
        "Navigate to Profile > Goals to update your weight targets, activity levels, dietary preferences, and fasting windows.",
    },
    {
      question: "Is my health data secure?",
      answer:
        "Absolutely. We use industry-standard encryption and never share your personal health data with third parties. Your privacy is our priority.",
    },
    {
      question: "How do I delete my account?",
      answer:
        "Go to Profile > Preferences and scroll to the bottom to find the account deletion option. Please note this action is irreversible.",
    },
  ];

  const gettingStartedSteps = [
    {
      step: 1,
      title: "Complete Your Profile",
      description:
        "Add your health details, goals, and preferences to personalize your experience.",
    },
    {
      step: 2,
      title: "Connect Your Device",
      description:
        "Link your Fitbit or fitness tracker to sync activity and sleep data automatically.",
    },
    {
      step: 3,
      title: "Start Scanning Meals",
      description:
        "Use the camera to scan your meals and track your nutrition effortlessly.",
    },
    {
      step: 4,
      title: "Review Your Insights",
      description:
        "Check your Bite Balance score and personalized recommendations on the home screen.",
    },
  ];

  const filteredFaqs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="mx-auto max-w-md space-y-5 p-4">
        {/* Header */}
        <div className="flex items-center pt-2">
          <button
            onClick={() => navigate("/profile")}
            className="rounded-full p-2 transition-colors hover:bg-muted"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="ml-2">
            <h1 className="text-xl font-bold text-foreground">
              Help & Support
            </h1>
            <p className="text-xs text-muted-foreground">
              Find answers and get assistance
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search for help..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="h-12 rounded-xl border-0 bg-muted/50 pl-10"
          />
        </div>

        {/* Getting Started Guide */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Getting Started</h3>
              <p className="text-xs text-muted-foreground">
                Quick guide to Bite Balance
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {gettingStartedSteps.map(item => (
              <div key={item.step} className="flex gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-bold text-primary">
                    {item.step}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground">
                    {item.title}
                  </h4>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* FAQs */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="bg-chart-2/10 flex h-10 w-10 items-center justify-center rounded-xl">
              <HelpCircle className="text-chart-2 h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                Frequently Asked Questions
              </h3>
              <p className="text-xs text-muted-foreground">
                {filteredFaqs.length} answers available
              </p>
            </div>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {filteredFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-border/50 last:border-0"
              >
                <AccordionTrigger className="py-3 text-left text-sm font-medium hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-3 text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredFaqs.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No matching questions found. Try a different search term.
            </p>
          )}
        </Card>

        {/* Contact Support */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="bg-chart-4/10 flex h-10 w-10 items-center justify-center rounded-xl">
              <MessageSquare className="text-chart-4 h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                Still Need Help?
              </h3>
              <p className="text-xs text-muted-foreground">
                Get in touch with our team
              </p>
            </div>
          </div>

          <Button variant="outline" className="h-12 w-full gap-2 rounded-xl">
            <Mail className="h-5 w-5" />
            <span>Email Us</span>
          </Button>
        </Card>

        {/* App Version */}
        <Card className="rounded-2xl border-0 bg-muted/30 p-4 shadow-sm">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">App Version</span>
            <span className="font-medium text-foreground">1.0.0</span>
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Last Updated</span>
            <span className="font-medium text-foreground">Dec 24, 2025</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HelpSupport;
